// ---------------------------------------------------------------------------
// The panel that hangs off a header button.
//
// WHY THIS EXISTS AT ALL. Both header dropdowns used to be absolutely
// positioned inside the header, and the header is `overflow-hidden` - which it
// has to be, or a long shop name pushes the whole document sideways on a
// phone. An `overflow-hidden` ancestor CLIPS a descendant however high its
// z-index is: z-index orders things that are painted, and a clipped box is
// never painted at all. That is why the notification list and the account menu
// appeared to sit behind the page.
//
// So the panel is rendered through a PORTAL into `document.body`, where it has
// no clipping ancestor, no transformed ancestor, and no stacking context above
// it. Position `fixed`, placed from the trigger's own bounding rectangle.
//
// ANCHORING WITHOUT MEASURING THE PANEL. It would be natural to compute
// `left = rect.right - panelWidth`, which needs the panel's width, which is
// not known until after it has been laid out - so the first frame is in the
// wrong place and it visibly jumps. Instead the panel is pinned to the
// VIEWPORT edge nearest the button: in LTR its right edge is `innerWidth -
// rect.right` from the right; in RTL its left edge is `rect.left` from the
// left. Both are known before the panel exists, and both put the panel's end
// edge under the button's end edge, which is what a header dropdown wants in
// either direction.
//
// ONE AT A TIME. A module-level registry closes whatever else was open when a
// new panel opens, so the account menu and the notification list can never
// overlap each other.
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { useI18n } from '../../i18n/I18nProvider';

/// Well above the app shell (the header is z-20, modals are z-95 and above)
/// and above anything a browser extension is likely to inject.
const PANEL_Z_INDEX = 99999;

/// The gap between the button and the panel, and the margin kept from the
/// edges of the screen so a panel never sits flush against them.
const GAP_PX = 6;
const EDGE_PX = 8;

// ---------------------------------------------------------------------------
// Only one open at a time
// ---------------------------------------------------------------------------

type Closer = () => void;
const openPanels = new Map<string, Closer>();

function claim(id: string, close: Closer) {
  for (const [otherId, closeOther] of openPanels) {
    if (otherId !== id) closeOther();
  }
  openPanels.clear();
  openPanels.set(id, close);
}

function release(id: string) {
  openPanels.delete(id);
}

/// Only for tests, which need the registry empty between cases.
export function resetHeaderPopoversForTests(): void {
  openPanels.clear();
}

// ---------------------------------------------------------------------------

interface Placement {
  top: number;
  /// Exactly one of these is set, depending on the writing direction.
  left?: number;
  right?: number;
  maxHeight: number;
}

export function HeaderPopover({
  id,
  open,
  onClose,
  anchorRef,
  children,
  labelledBy,
  role = 'dialog',
  className,
  width = 'w-[min(22rem,calc(100vw-1rem))]',
}: {
  /// Distinguishes this panel in the one-at-a-time registry.
  id: string;
  open: boolean;
  onClose: () => void;
  /// The button the panel hangs from.
  anchorRef: React.RefObject<HTMLElement>;
  children: ReactNode;
  labelledBy?: string;
  role?: 'dialog' | 'menu';
  className?: string;
  width?: string;
}) {
  const { dir } = useI18n();
  const location = useLocation();
  const panelRef = useRef<HTMLDivElement>(null);
  const [placement, setPlacement] = useState<Placement | null>(null);

  /// Where the panel goes, worked out from the trigger.
  ///
  /// Runs twice for an opening panel: once before it exists, from the button
  /// alone, and again once it has been laid out and its width can be measured.
  /// The second pass is what keeps a panel wider than the space beside its
  /// button from hanging off the far edge - a bell near the middle of a narrow
  /// screen would otherwise pin a 22rem panel to the right of the bell and put
  /// its left edge past the left of the screen.
  const place = useCallback(() => {
    const anchor = anchorRef.current;
    if (!anchor) return;

    const rect = anchor.getBoundingClientRect();
    const top = Math.round(rect.bottom + GAP_PX);

    // Zero until the panel has been laid out. A zero width simply means the
    // clamps below cannot bind yet, which is correct: there is nothing to
    // overflow with.
    const panelWidth = panelRef.current?.getBoundingClientRect().width ?? 0;
    // The furthest the near edge can sit from its own side before the far
    // edge leaves the screen.
    const furthest = Math.max(EDGE_PX, Math.round(window.innerWidth - panelWidth - EDGE_PX));

    setPlacement({
      top,
      // Pinned to the viewport edge on the button's END side, so the panel
      // opens inwards in both directions, then clamped so it stays wholly on
      // screen in either direction.
      ...(dir === 'rtl'
        ? { left: Math.min(furthest, Math.max(EDGE_PX, Math.round(rect.left))) }
        : {
            right: Math.min(
              furthest,
              Math.max(EDGE_PX, Math.round(window.innerWidth - rect.right)),
            ),
          }),
      // Whatever is left below the button, so a long notification list
      // scrolls inside itself rather than running off the bottom.
      maxHeight: Math.max(160, Math.round(window.innerHeight - top - EDGE_PX)),
    });
  }, [anchorRef, dir]);

  // Placed BEFORE the browser paints, so it never appears in the wrong spot.
  useLayoutEffect(() => {
    if (!open) {
      setPlacement(null);
      return;
    }
    place();
  }, [open, place]);

  // And again once it exists, now that its width can be measured. Still
  // before paint, so the correction is never seen as a jump. `placement`
  // going from null to set is what triggers this; `place` writing the same
  // numbers back is a no-op that React does not re-render for.
  useLayoutEffect(() => {
    if (!open || !placement) return;
    place();
    // Deliberately NOT depending on `placement`: this needs to run when the
    // panel first appears, not on every correction it makes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, Boolean(placement), place]);

  // Close whatever else was open, and give this one up when it closes.
  useEffect(() => {
    if (!open) return;
    claim(id, onClose);
    return () => release(id);
  }, [open, id, onClose]);

  // A route change closes it: navigating with a menu still hanging over the
  // new page is disorienting, and on a till it happens by accident.
  useEffect(() => {
    if (open) onClose();
    // Only the path matters here; re-running on `onClose` would close it the
    // moment it opened.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Outside click, Escape, and anything that moves the trigger.
  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      // The trigger is excluded as well as the panel, or its own click would
      // close and immediately reopen.
      if (panelRef.current?.contains(target)) return;
      if (anchorRef.current?.contains(target)) return;
      onClose();
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    // A fixed panel does not follow its trigger, so it is re-placed when
    // anything scrolls - `true` to catch scrolling inside the page, not only
    // on the window.
    const reposition = () => place();

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);

    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [open, onClose, place, anchorRef]);

  if (!open || !placement || typeof document === 'undefined') return null;

  return createPortal(
    <div
      ref={panelRef}
      role={role}
      aria-labelledby={labelledBy}
      dir={dir}
      style={{
        position: 'fixed',
        top: placement.top,
        left: placement.left,
        right: placement.right,
        maxHeight: placement.maxHeight,
        zIndex: PANEL_Z_INDEX,
      }}
      className={
        // The shop's own panel styling, not a second look invented here.
        `flex ${width} animate-fade-in flex-col overflow-hidden rounded-panel border border-line bg-surface shadow-raised ${className ?? ''}`
      }
    >
      {children}
    </div>,
    document.body,
  );
}
